import React, { useState, useEffect } from "react";
import {useDispatch, useSelector} from "react-redux";
import {useHistory} from "react-router-dom";
import { useFormContext } from "react-hook-form";
import {
    createTicket,
} from "../../actions/tickets";
import {
    PaymentElement,
    Elements,
    useStripe,
    useElements
} from "@stripe/react-stripe-js";
import {loadStripe} from "@stripe/stripe-js";
import StripeService from "../../services/StripeService";

import AcceptTerms from "./AcceptTerms";
import Spinner from "../Spinner.js";
import { PaymentRetrieval } from "./swishHelpers/PaymentRetrieval";
import NewsletterService from "../../services/NewsletterService.js";

// Inner form component that uses the Stripe hooks
function PaymentForm({ clientSecret, onPaymentSuccess, onPaymentError, bookingContext }) {
    const [stripeError, setStripeError] = useState(null);
    const [processing, setProcessing] = useState(false);
    const [ready, setReady] = useState(false);
    const stripe = useStripe();
    const elements = useElements();
    const {handleSubmit, getValues} = useFormContext();

    const submitPayment = async (data) => {
        if (!stripe || !elements) {
            return;
        }

        const { travelers: [passenger], email } = data;
        const { firstName, lastName } = passenger;
        setProcessing(true);
        setStripeError(null);

        // Stash booking data server-side, keyed by PaymentIntent id, before
        // redirect-based payments (Swish, Klarna). The webhook/return page
        // completes the booking from it - browser storage does not reliably
        // survive the app switch, so nothing may be kept only in the browser.
        try {
            const paymentIntentId = clientSecret.split('_secret')[0];
            await StripeService.storePendingBooking(paymentIntentId, {
                formData: data,
                bookingContext: bookingContext
            });
        } catch (stashError) {
            console.error('Could not store pending booking:', stashError);
            setStripeError('Något gick fel. Försök igen.');
            setProcessing(false);
            return;
        }

        const { error, paymentIntent } = await stripe.confirmPayment({
            elements,
            confirmParams: {
                return_url: window.location.origin + '/confirmation',
                receipt_email: email,
                payment_method_data: {
                    billing_details: {
                        email: email,
                        name: firstName + ' ' + lastName
                    }
                }
            },
            redirect: 'if_required'
        });

        if (error) {
            setStripeError(error.message);
            setProcessing(false);
            onPaymentError(error);
        } else if (paymentIntent && paymentIntent.status === 'succeeded') {
            onPaymentSuccess(paymentIntent, data);
        } else if (paymentIntent && paymentIntent.status === 'processing') {
            // Payment is processing (common with Swish)
            onPaymentSuccess(paymentIntent, data);
        } else {
            setStripeError('Betalningen kunde inte genomföras. Försök igen.');
            setProcessing(false);
        }
    };

    const onSubmit = (data, e) => {
        e.preventDefault();
        submitPayment(data);
    };

    return (
        <form id="payment-form" onSubmit={handleSubmit(onSubmit)}>
            <div className="py-4 my-4">
                <PaymentElement
                    id="payment-element"
                    onReady={() => setReady(true)}
                    options={{
                        layout: 'tabs'
                    }}
                />
            </div>
            <AcceptTerms/>
            <button
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-gray-400"
                disabled={processing || !ready || !stripe}
                id="submit"
            >
                <span id="button-text">
                    {processing ? (
                        <Spinner />
                    ) : (
                        "Betala"
                    )}
                </span>
            </button>
            {stripeError && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm" role="alert">
                    {stripeError}
                </div>
            )}
        </form>
    );
}

// Main checkout component that handles payment intent creation
export default function CheckoutForm() {
    const [clientSecret, setClientSecret] = useState(null);
    const [stripePromise, setStripePromise] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const dispatch = useDispatch();
    const history = useHistory();
    const {getValues} = useFormContext();

    const train = useSelector(state => state.trains.currentTrain);
    const station = useSelector(state => state.activeStation);
    const travelers = useSelector(state => state.travelers);
    const { seats } = useSelector(state => state.activeSeats);
    const price = useSelector(state => state.price);
    const discount = useSelector(state => state.discount);
    const sessionId = useSelector(state => state.session);
    let $HR = PaymentRetrieval;

    // Stripe.js is initialised once the payment intent exists: the intent is
    // a direct charge on the tenant's connected account, and Stripe.js must
    // be bound to that same account (stripeAccount) to confirm it.

    // Create payment intent when component mounts
    useEffect(() => {
        const createIntent = async () => {
            if (!train || !station || !travelers || !seats || !price) {
                return;
            }

            setLoading(true);
            setError(null);

            try {
                const res = await StripeService.createPaymentIntent(JSON.stringify({
                    train,
                    station,
                    travelers,
                    seats,
                    price,
                    discount: {
                        code: discount ? discount.code : null
                    }
                }));

                if (res?.clientSecret) {
                    const platformStripeKey = process.env.MIX_STRIPE_PUBLIC_KEY;
                    if (platformStripeKey) {
                        setStripePromise(loadStripe(
                            platformStripeKey,
                            res.stripeAccount ? { stripeAccount: res.stripeAccount } : undefined
                        ));
                    }
                    setClientSecret(res.clientSecret);
                } else {
                    setError('Kunde inte skapa betalning. Försök igen.');
                }
            } catch (err) {
                setError('Ett fel uppstod. Försök igen eller kontakta oss.');
                console.error('Payment intent error:', err);
            } finally {
                setLoading(false);
            }
        };

        createIntent();
    }, [train, station, travelers, seats, price, discount]);

    const handlePaymentSuccess = async (paymentIntent, formData) => {
        try {
            await dispatch(createTicket(
                {
                    train,
                    station,
                    seats,
                    travelers: formData,
                    discount: {
                        code: discount ? discount.code : null
                    },
                    payment: {
                        status: paymentIntent.status,
                        id: paymentIntent.id,
                        reference: paymentIntent.payment_method || 'N/A',
                        method: $HR.PaymentMethod.STRIPE
                    }
                }, sessionId));

            // Handle newsletter subscription
            if (formData.subscribe) {
                const { travelers: [passenger], email, phone } = formData;
                await NewsletterService.addSubscriber(JSON.stringify({passenger, email, phone}));
            }

            // The confirmation page reads everything from the query parameter,
            // so the receipt survives reloads and shared links.
            history.push('/confirmation?payment_intent=' + paymentIntent.id);
        } catch (error) {
            if (error.response && error.response.status === 409) {
                setError('En eller flera av de valda platserna är inte längre tillgängliga. Vänligen välj andra platser.');
                setTimeout(() => history.push(`/trains/${train.id}/seats`), 3000);
            } else {
                setError('Ett fel uppstod vid bokningen. Vänligen försök igen.');
            }
        }
    };

    const handlePaymentError = (error) => {
        console.error('Payment error:', error);
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center py-8">
                <Spinner />
                <span className="ml-2 text-gray-600">Laddar betalningsalternativ...</span>
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-4 bg-red-50 border border-red-200 rounded-md text-red-700">
                {error}
            </div>
        );
    }

    if (!clientSecret || !stripePromise) {
        return (
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-md text-yellow-700">
                Betalning är inte tillgänglig just nu. Försök igen senare.
            </div>
        );
    }

    const elementsOptions = {
        clientSecret,
        appearance: {
            theme: 'stripe',
            variables: {
                colorPrimary: '#4f46e5',
                colorBackground: '#ffffff',
                colorText: '#1f2937',
                colorDanger: '#dc2626',
                fontFamily: 'system-ui, -apple-system, sans-serif',
                borderRadius: '6px',
            },
        },
        locale: 'sv'
    };

    // Create booking context object to pass to PaymentForm for sessionStorage
    const bookingContext = {
        train,
        station,
        seats,
        discount: discount ? { code: discount.code } : null,
        sessionId
    };

    return (
        <Elements stripe={stripePromise} options={elementsOptions}>
            <PaymentForm
                clientSecret={clientSecret}
                onPaymentSuccess={handlePaymentSuccess}
                onPaymentError={handlePaymentError}
                bookingContext={bookingContext}
            />
        </Elements>
    );
}